import { useState, useCallback } from "react";

/**
 * Hook personalizado para copiar texto al portapapeles con feedback visual
 * @returns {Object} - Objeto con la función de copiar, el estado de copiado y la función de reset
 */
export const useCopyToClipboard = () => {
  const [copiedId, setCopiedId] = useState(null);
  const [error, setError] = useState(null);

  /**
   * Copia el texto al portapapeles
   * @param {string} text - Texto a copiar
   * @param {string} id - Identificador único para el elemento que se está copiando
   * @param {number} timeout - Tiempo en ms para mostrar el feedback (default: 2000)
   */
  const copyToClipboard = useCallback(async (text, id, timeout = 2000) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setError(null);

      // Limpiar el estado después del timeout
      setTimeout(() => {
        setCopiedId(null);
      }, timeout);

      return true;
    } catch (err) {
      console.error("Error al copiar al portapapeles:", err);
      setError("No se pudo copiar al portapapeles");
      return false;
    }
  }, []);

  /**
   * Resetea manualmente el estado de copiado
   */
  const reset = useCallback(() => {
    setCopiedId(null);
    setError(null);
  }, []);

  /**
   * Verifica si un elemento específico fue copiado
   * @param {string} id - Identificador del elemento
   * @returns {boolean}
   */
  const isCopied = useCallback(
    (id) => {
      return copiedId === id;
    },
    [copiedId]
  );

  return {
    copyToClipboard,
    copiedId,
    isCopied,
    error,
    reset,
  };
};

export default useCopyToClipboard;
